import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

export default function Lightbox({ project, onClose }) {
  const images = project ? [...new Set([project.image, ...(project.images || [])].filter(Boolean))] : [];
  const [idx, setIdx] = useState(0);

  useEffect(() => setIdx(0), [project]);

  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, next, prev, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm"
          data-testid="lightbox"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 py-5 md:px-10">
            <div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-lime">{project.category}</span>
              <h3 className="mt-1 font-serif text-2xl text-white md:text-3xl">{project.title}</h3>
              <p className="mt-1 text-sm text-white/50">
                {[project.client, project.city, project.year, project.guests].filter(Boolean).join(" · ")}
              </p>
              {(project.objective || project.result) && (
                <div className="mt-3 flex max-w-2xl flex-col gap-1.5 text-sm" data-testid="lightbox-details">
                  {project.objective && <p className="text-white/70"><span className="font-medium text-lime">Objectif — </span>{project.objective}</p>}
                  {project.result && <p className="text-white/70"><span className="font-medium text-lime">Résultat — </span>{project.result}</p>}
                </div>
              )}
              <Link
                to="/devis"
                onClick={onClose}
                className="mt-4 inline-flex items-center gap-2 self-start rounded-full bg-lime px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-lime-dim hover:text-white"
                data-testid="lightbox-devis-btn"
              >
                Recevoir une proposition pour mon événement <ArrowRight size={15} />
              </Link>
            </div>
            <button onClick={onClose} className="rounded-full border border-white/20 p-2 text-white transition-colors hover:bg-white hover:text-black" data-testid="lightbox-close" aria-label="Fermer">
              <X size={22} />
            </button>
          </div>

          {/* Image */}
          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-4 md:px-16">
            <AnimatePresence mode="wait">
              <motion.img
                key={idx}
                src={images[idx]}
                alt={`${project.title} ${idx + 1}`}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="max-h-full max-w-full rounded-xl object-contain"
                data-testid="lightbox-image"
              />
            </AnimatePresence>

            {images.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white transition-colors hover:bg-lime hover:text-black md:left-6" data-testid="lightbox-prev" aria-label="Précédent"><ChevronLeft size={24} /></button>
                <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white transition-colors hover:bg-lime hover:text-black md:right-6" data-testid="lightbox-next" aria-label="Suivant"><ChevronRight size={24} /></button>
              </>
            )}
          </div>

          {/* Thumbnails / counter */}
          <div className="flex items-center justify-center gap-2 pb-6">
            {images.length > 1 ? images.map((img, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-lime" : "w-4 bg-white/30 hover:bg-white/60"}`} aria-label={`Image ${i + 1}`} />
            )) : null}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
